import { ValueEqualityFn } from '@angular/core'
import {
	signalImplementation,
	WritableSignal,
	CreateComputedOptions,
	SignalImplementation,
	CreateSignalOptions,
	Signal,
} from './signal-implementation.js'

export interface ForcibleWritableSignal<T> extends WritableSignal<T> {
	forceUpdate(): void
	forceSet(value: T): void
}

export interface ForcibleSignal<T> extends Signal<T> {
	forceUpdate(): void
}

const signalsToUpdate = new WeakSet<Signal<any>>()

export function forcibleSignal<T>(
	value: T,
	options?: CreateSignalOptions<T>,
): ForcibleWritableSignal<T> {
	if (!signalImplementation)
		return (() => {
			throw new Error('Signal implementation not set')
		}) as any as ForcibleWritableSignal<T>

	const underlyingSignal = signalImplementation!.signal(value, {
		...options,
		equal: (a, b) => {
			if (signalsToUpdate.has(underlyingSignal)) {
				signalsToUpdate.delete(underlyingSignal)
				return false
			}

			if (options?.equal) return options.equal(a, b)
			return Object.is(a, b)
		},
	}) as ForcibleWritableSignal<T>

	underlyingSignal.forceUpdate = () => {
		underlyingSignal.forceSet(underlyingSignal())
	}
	underlyingSignal.forceSet = (newValue: T) => {
		signalsToUpdate.add(underlyingSignal)
		underlyingSignal.set(newValue)
	}

	return underlyingSignal
}

export function forcibleComputed<T>(
	computation: () => T,
	options?: CreateComputedOptions<T>,
): ForcibleSignal<T> {
	if (!signalImplementation)
		return (() => {
			throw new Error('Signal implementation not set')
		}) as any as ForcibleSignal<T>

	const updateSignal = forcibleSignal<void>(undefined)
	const underlyingSignal = signalImplementation!.computed(
		() => {
			updateSignal() // ensure that the update signal is always subscribed to
			return computation()
		},
		{
			...options,
			equal: (a, b) => {
				if (signalsToUpdate.has(underlyingSignal)) {
					signalsToUpdate.delete(underlyingSignal)
					return false
				}

				if (options?.equal) return options.equal(a, b)
				return Object.is(a, b)
			},
		},
	) as ForcibleSignal<T>

	underlyingSignal.forceUpdate = () => {
		signalsToUpdate.add(underlyingSignal)
		updateSignal.forceUpdate()
	}

	return underlyingSignal
}
